import { Component, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { BehaviorSubject } from 'rxjs';
import { StrapiService } from '@core/services';
import { SafeHtmlPipe } from '@core/pipes';

interface InterestedFeature {
  Title: string;
  Perks: string[];
  Icon: string;
}

interface InterestedPageContent {
  Heading: string;
  Subheading: string;
  Buzzwords: string[];
  Features: InterestedFeature[];
}

@Component({
  selector: 'oke-interested',
  imports: [RouterLink, SvgIconComponent, SafeHtmlPipe],
  templateUrl: './interested.html',
  styleUrl: './interested.scss'
})
export class Interested implements OnInit, OnDestroy {
  contentSource = new BehaviorSubject<InterestedPageContent>({
    Heading: 'The ONEOK Customer&nbsp;Portal',
    Subheading: 'Serving anytime information for your business needs.',
    Buzzwords: ['instant', 'anytime'],
    Features: [
      {
        Title: 'Easy to Access',
        Perks: ['<p>Secure access from any internet device</p>'],
        Icon: '/assets/svg/laptop-mobile.svg'
      },
      {
        Title: 'Easy to Manage',
        Perks: ['<p>Manage your Customer Portal and myQuorum account needs</p>'],
        Icon: '/assets/svg/key.svg'
      },
      {
        Title: 'Easy to Use',
        Perks: ['<p>Simple navigation, help and access to your information</p>'],
        Icon: '/assets/svg/face-smile.svg'
      }
    ]
  });
  content$ = this.contentSource.asObservable();
  private readonly _msAct1 = 6000;
  private readonly _numBreakers = 2;
  private _keywords: string[] = ['instant', 'anytime'];
  private _keywordUpNextIdx = 1;
  private readonly _componentTimeout?: number;
  private readonly _componentInterval?: number;
  private _launchTimeout?: number;
  private readonly _elem = inject(ElementRef<HTMLElement>);
  private readonly _strapiSvc = inject(StrapiService);
  heading = 'The ONEOK Customer&nbsp;Portal';
  subheading = 'Serving anytime information for your business needs.';
  features: InterestedFeature[] = [
    {
      Title: 'Easy to Access',
      Perks: ['<p>Secure access from any internet device</p>'],
      Icon: '/assets/svg/laptop-mobile.svg'
    },
    {
      Title: 'Easy to Manage',
      Perks: ['<p>Manage your Customer Portal and myQuorum account needs</p>'],
      Icon: '/assets/svg/key.svg'
    },
    {
      Title: 'Easy to Use',
      Perks: ['<p>Simple navigation, help and access to your information</p>'],
      Icon: '/assets/svg/face-smile.svg'
    }
  ];
  dynamicKeyword = 'anytime';
  isAct1 = true;
  isShowingDetail = false;
  shownDetailIdx?: number;

  constructor() {
    this.content$.subscribe(this._onContentDefined.bind(this));
    this._getContent();
  }

  private _onBreakersAnimationEnd(event: AnimationEvent): void {
    void event;
    this._elem.nativeElement.classList.remove('act-1');
    this.isAct1 = false;
    const primaryElem = this._elem.nativeElement.querySelector('.primary') as HTMLElement | null;
    primaryElem?.removeAttribute('style');
  }

  private _onPrimaryAnimationEnd(event: AnimationEvent): void {
    void event;
    const primaryElem = this._elem.nativeElement.querySelector('.primary') as HTMLElement | null;
    primaryElem?.removeEventListener('animationend', (evt: AnimationEvent) => this._onPrimaryAnimationEnd(evt));
    const now = Date.now();
    const progressElem = this._elem.nativeElement.querySelector('progress') as HTMLProgressElement | null;
    const eachBreakerElem = (breakerElem: Element): void => {
      breakerElem.classList.add('is-animating');
    };
    const hold = (): void => {
      if (!progressElem) {
        return;
      }
      const elapsedMs = Date.now() - now;
      const progressValue = Math.round((elapsedMs / this._msAct1) * 100);
      const isStillGoing = elapsedMs < this._msAct1;
      progressElem.value = progressValue;
      if (isStillGoing) {
        requestAnimationFrame(hold);
      } else {
        Array.from(this._elem.nativeElement.querySelectorAll('.breaker') as NodeListOf<Element>).forEach(
          eachBreakerElem
        );
      }
    };
    hold();
  }

  /*private initKeywordAnimation(): void {
      const dynamicKeywordContainer = this._elem.nativeElement.querySelector( ".primary .is-dynamic span" );
      let initialDynamicKeywordElem = dynamicKeywordContainer.querySelector( "strong" );
      const onDynamicKeywordElemAnimationEnd = ({ target: keywordElem }: { target: HTMLElement}) => {
          const isOut = keywordElem.classList.contains( "post" );
          if( isOut ){
              initialDynamicKeywordElem.removeEventListener( "animationend", onDynamicKeywordElemAnimationEnd );
              initialDynamicKeywordElem = keywordElem.nextElementSibling;
              keywordElem.parentElement.removeChild( keywordElem );
              initialDynamicKeywordElem.addEventListener( "animationend", onDynamicKeywordElemAnimationEnd );
          }
      };
      initialDynamicKeywordElem.addEventListener( "animationend", onDynamicKeywordElemAnimationEnd );
      this._componentInterval = window.setInterval( this._onDynamicKeywordInterval.bind( this ), 3000 );
  }*/

  private _onDynamicKeywordInterval(): void {
    const dynamicKeywordContainer = this._elem.nativeElement.querySelector(
      '.primary .is-dynamic span'
    ) as HTMLElement | null;
    if (!dynamicKeywordContainer) {
      return;
    }

    const appendedKeywordElem = document.createElement('strong');
    const getKeywordInlineSizeRems = (keywordElem: HTMLElement): string => {
      const px = parseInt(window.getComputedStyle(keywordElem).inlineSize, 10);
      const rem = px / 16;
      return `${rem}rem`;
    };
    const initialDynamicKeywordElem = dynamicKeywordContainer.querySelector('strong') as HTMLElement | null;
    if (!initialDynamicKeywordElem) {
      return;
    }

    appendedKeywordElem.className = 'pre';
    appendedKeywordElem.textContent = this._keywords[this._keywordUpNextIdx];
    this._keywordUpNextIdx = this._keywords[this._keywordUpNextIdx + 1] ? this._keywordUpNextIdx + 1 : 0;
    dynamicKeywordContainer.appendChild(appendedKeywordElem);
    dynamicKeywordContainer.style.setProperty('--dynamic-width', getKeywordInlineSizeRems(appendedKeywordElem));
    initialDynamicKeywordElem.className = 'post';
  }

  get breakers(): unknown[] {
    return new Array(this._numBreakers);
  }

  ngOnInit(): void {
    document.body.classList.add('is-unauthenticated', 'interested');
    this._elem.nativeElement.classList.add('act-1');
  }

  ngOnDestroy(): void {
    window.clearInterval(this._componentInterval);
    window.clearTimeout(this._componentTimeout);
    document.body.classList.remove('is-unathenticated', 'interested');
  }

  private _onContentDefined(content: InterestedPageContent | null | undefined): void {
    if (!content) {
      return;
    }

    this.heading = content.Heading;
    this._keywords = content.Buzzwords;
    this.dynamicKeyword = content.Buzzwords[0];
    // this.subheading = content.Subheading.replace(/#.*#/g, "<span><strong>"+ this.dynamicKeyword +"</strong></span>");
    this.features = content.Features.map(({ Icon: icon, Perks: perks, Title: title }) => ({
      Icon: icon,
      Title: title,
      Perks: perks.map((perk: string) => perk.replace('<p>', '').replace('</p>', ''))
    }));
    this._launchTimeout = window.setTimeout(() => this.launch(), 500);
  }

  private _getContent(): void {
    const content = JSON.parse(
      (sessionStorage.getItem('interestedContent') ?? 'null') as string
    ) as InterestedPageContent | null;
    if (!content) {
      this._strapiSvc.getInterestedContent().subscribe((strapiInterestedContent: InterestedPageContent) => {
        sessionStorage.setItem('interestedContent', JSON.stringify(strapiInterestedContent));
        this.contentSource.next(strapiInterestedContent);
      });
    } else {
      this.contentSource.next(content);
    }
  }

  launch(): void {
    if (this._launchTimeout) {
      window.clearTimeout(this._launchTimeout);
    }

    const primaryElem = this._elem.nativeElement.querySelector('.primary') as HTMLElement | null;
    if (!primaryElem) {
      return;
    }

    primaryElem.addEventListener('animationend', (evt: AnimationEvent) => this._onPrimaryAnimationEnd(evt));
    if (this._numBreakers > 1) {
      const breakerOneElem = this._elem.nativeElement.querySelector('.breaker--1') as HTMLElement | null;
      breakerOneElem?.addEventListener('animationend', () => {
        const activePrimaryElem = this._elem.nativeElement.querySelector('.primary') as HTMLElement | null;
        if (activePrimaryElem) {
          activePrimaryElem.style.opacity = '0';
        }
      });
      const breakerLastElem = this._elem.nativeElement.querySelector(
        `.breaker--${this._numBreakers}`
      ) as HTMLElement | null;
      breakerLastElem?.addEventListener('animationend', (evt: AnimationEvent) => this._onBreakersAnimationEnd(evt));
    } else {
      const breakerElem = this._elem.nativeElement.querySelector(
        `.breaker--${this._numBreakers}`
      ) as HTMLElement | null;
      breakerElem?.addEventListener('animationend', (evt: AnimationEvent) => this._onBreakersAnimationEnd(evt));
    }
  }

  onSelectMatrixChild(clickEvt: Event): void {
    const parentElement = (clickEvt.target as HTMLButtonElement).parentElement;
    if (!parentElement) {
      return;
    }
    const allButtonElems = Array.from(parentElement.children);
    allButtonElems.forEach(buttonElem => buttonElem.classList.remove('is-selected'));
    (clickEvt.target as HTMLButtonElement).classList.add('is-selected');
    this.isShowingDetail = true;
    this.shownDetailIdx = allButtonElems.findIndex(buttonElem => buttonElem === (clickEvt.target as HTMLButtonElement));
  }
}
